import { useState } from 'react';
import { BUILT_IN_SOUNDS, LANGUAGES, Prompt, PromptRef } from '../../lib/ivr';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { loadReference } from '../../lib/reference';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad, RetentionField } from './kit';

type Strategy = 'longest_idle' | 'least_calls' | 'round_robin' | 'ring_all';
interface QueueRow {
  id: string;
  name: string;
  strategy: Strategy;
  priority: number;
  sla_seconds: number;
  sla_target_pct: number;
  max_wait_seconds: number;
  wrap_seconds: number;
  ring_seconds: number;
  record_policy: 'all' | 'none';
  channel: 'voice' | 'chat' | 'email';
  recording_retention_days: number | null;
  overflow_action: AwayAction;
  overflow_queue_id: string | null;
  overflow_number: string | null;
  closed_action: AwayAction;
  closed_queue_id: string | null;
  closed_number: string | null;
  closed_prompt: PromptRef | null;
  recording_notice: PromptRef | null;
  emergency_closed: boolean;
  require_disposition: boolean;
  business_hours_id: string | null;
  skills: { skillId: string; skill: string; minLevel: number }[];
}
interface Named {
  id: string;
  name: string;
}

const CHANNEL_LABEL: Record<QueueRow['channel'], string> = { voice: 'Voice', chat: 'Livechat', email: 'Email' };

type AwayAction = 'hangup' | 'voicemail' | 'queue' | 'number';
const AWAY: { id: AwayAction; label: string }[] = [
  { id: 'hangup', label: 'Say goodbye and end the call' },
  { id: 'voicemail', label: 'Take a voicemail' },
  { id: 'queue', label: 'Send them to another queue' },
  { id: 'number', label: 'Ring an outside number' },
];

const STRATEGIES: { id: Strategy; label: string; hint: string }[] = [
  { id: 'longest_idle', label: 'Longest idle', hint: 'The agent who has waited longest since their last call' },
  { id: 'least_calls', label: 'Fewest calls today', hint: 'Spreads calls evenly across the shift' },
  { id: 'round_robin', label: 'Round robin', hint: 'Takes turns in order' },
];

export function Queues() {
  const [queues, reload] = useLoad<QueueRow[]>('/queues');
  const [skills] = useLoad<Named[]>('/skills');
  const [hours] = useLoad<Named[]>('/business-hours');
  const [editing, setEditing] = useState<QueueRow | 'new' | null>(null);
  const hoursName = (id: string | null) => hours?.find((h) => h.id === id)?.name;

  const done = () => {
    setEditing(null);
    reload();
    void loadReference();
  };

  return (
    <>
      <SectionHeader
        title="Queues"
        lead="Where callers wait. Each number points at a queue; the queue decides who is rung, how long callers wait and whether calls are recorded."
        action={<AddButton onClick={() => setEditing('new')}>Add queue</AddButton>}
      />
      <Table head={['Queue', 'Service level', 'Who is rung', 'Open', 'Wrap-up', 'Recording']}>
        {(queues ?? []).map((q) => (
          <Row key={q.id} onClick={() => setEditing(q)}>
            <Cell>
              <span className="font-medium">{q.name}</span>
              <span className="block text-xs text-ink-3">
                {CHANNEL_LABEL[q.channel ?? 'voice']} · Priority {q.priority}
              </span>
            </Cell>
            <Cell>
              {q.sla_target_pct}% in {q.sla_seconds}s
            </Cell>
            <Cell>
              <span className="block">{STRATEGIES.find((s) => s.id === q.strategy)?.label ?? q.strategy}</span>
              <span className="mt-1 flex flex-wrap gap-1">
                {q.skills.length ? q.skills.map((s) => <Pill key={s.skillId}>{`${s.skill} ${s.minLevel}+`}</Pill>) : <span className="text-xs text-ink-3">Anyone with an extension</span>}
              </span>
            </Cell>
            <Cell>{hoursName(q.business_hours_id) ?? <span className="text-ink-3">Always</span>}</Cell>
            <Cell>
              {q.wrap_seconds}s{q.require_disposition ? <span className="block text-xs text-ink-3">Outcome required</span> : null}
            </Cell>
            <Cell>{q.channel !== 'voice' ? <span className="text-ink-3">–</span> : q.record_policy === 'all' ? <Pill tone="good">Every call</Pill> : <Pill>Off</Pill>}</Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <QueueDrawer queue={editing === 'new' ? null : editing} queues={queues ?? []} skills={skills ?? []} hours={hours ?? []} onClose={() => setEditing(null)} onDone={done} />
      ) : null}
    </>
  );
}

function QueueDrawer({
  queue,
  queues,
  skills,
  hours,
  onClose,
  onDone,
}: {
  queue: QueueRow | null;
  queues: QueueRow[];
  skills: Named[];
  hours: Named[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [f, setF] = useState({
    name: queue?.name ?? '',
    strategy: (queue?.strategy === 'ring_all' ? 'longest_idle' : queue?.strategy) ?? ('longest_idle' as Strategy),
    priority: queue?.priority ?? 5,
    slaSeconds: queue?.sla_seconds ?? 20,
    slaTargetPct: queue?.sla_target_pct ?? 80,
    maxWaitSeconds: queue?.max_wait_seconds ?? 600,
    wrapSeconds: queue?.wrap_seconds ?? 30,
    ringSeconds: queue?.ring_seconds ?? 20,
    recordPolicy: queue?.record_policy ?? 'all',
    channel: queue?.channel ?? ('voice' as QueueRow['channel']),
    recordingRetentionDays: queue?.recording_retention_days ?? null,
    overflowAction: queue?.overflow_action ?? ('hangup' as AwayAction),
    overflowQueueId: queue?.overflow_queue_id ?? '',
    overflowNumber: queue?.overflow_number ?? '',
    closedAction: queue?.closed_action ?? ('hangup' as AwayAction),
    closedQueueId: queue?.closed_queue_id ?? '',
    closedNumber: queue?.closed_number ?? '',
    closedPrompt: queue?.closed_prompt ?? null,
    recordingNotice: queue?.recording_notice ?? null,
    requireDisposition: queue?.require_disposition ?? true,
    businessHoursId: queue?.business_hours_id ?? '',
  });
  const [mins, setMins] = useState<Record<string, number>>(Object.fromEntries((queue?.skills ?? []).map((s) => [s.skillId, s.minLevel])));
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function save() {
    setBusy(true);
    const body = {
      ...f,
      businessHoursId: f.businessHoursId || null,
      overflowQueueId: f.overflowAction === 'queue' ? f.overflowQueueId || null : null,
      overflowNumber: f.overflowAction === 'number' ? f.overflowNumber.replace(/[^\d+]/g, '') || null : null,
      closedQueueId: f.closedAction === 'queue' ? f.closedQueueId || null : null,
      closedNumber: f.closedAction === 'number' ? f.closedNumber.replace(/[^\d+]/g, '') || null : null,
      skills: Object.entries(mins).map(([skillId, minLevel]) => ({ skillId, minLevel })),
    };
    const ok = await act(() => (queue ? api('PATCH', `/queues/${queue.id}`, body) : api('POST', '/queues', body)), queue ? 'Queue saved' : `${f.name} added`);
    setBusy(false);
    if (ok) onDone();
  }

  async function remove() {
    const ok = await act(() => api('DELETE', `/queues/${queue!.id}`).then(() => true), `${queue!.name} deleted`);
    if (ok) onDone();
  }

  const num = (k: 'priority' | 'slaSeconds' | 'slaTargetPct' | 'maxWaitSeconds' | 'wrapSeconds' | 'ringSeconds', label: string, hint: string, min: number, max: number) => (
    <Field label={label} hint={hint}>
      {(id) => (
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          className={`${inputClass} tabular`}
          value={f[k]}
          onChange={(e) => set(k, Math.max(min, Math.min(max, Number(e.target.value) || min)))}
        />
      )}
    </Field>
  );

  return (
    <Drawer
      title={queue ? `Edit ${queue.name}` : 'Add a queue'}
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={busy || f.name.trim().length < 2}>
            {busy ? 'Saving…' : queue ? 'Save changes' : 'Add queue'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          {queue ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Delete queue" onConfirm={() => void remove()}>
                Delete
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      <Field label="Name">{(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} />}</Field>
      <Field label="Channel" hint={queue ? 'The kind of work this queue holds. It cannot change once created.' : 'Voice calls, livechat or email. Each channel has its own queues.'}>
        {(id) => (
          <select id={id} className={inputClass} value={f.channel} disabled={Boolean(queue)} onChange={(e) => set('channel', e.target.value as QueueRow['channel'])}>
            <option value="voice">Voice</option>
            <option value="chat">Livechat</option>
            <option value="email">Email</option>
          </select>
        )}
      </Field>
      <Field label="Who is rung first" hint={STRATEGIES.find((s) => s.id === f.strategy)?.hint}>
        {(id) => (
          <select id={id} className={inputClass} value={f.strategy} onChange={(e) => set('strategy', e.target.value as Strategy)}>
            {STRATEGIES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        )}
      </Field>

      <p className="mb-2 text-sm font-medium">Required skills</p>
      <p className="mb-3 text-xs text-ink-3">Leave all unticked to ring anyone with an extension.</p>
      <ul className="mb-5 flex flex-col gap-2">
        {skills.map((s) => {
          const min = mins[s.id];
          return (
            <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2">
              <label className="flex items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={min !== undefined}
                  onChange={(e) =>
                    setMins((m) => {
                      const next = { ...m };
                      if (e.target.checked) next[s.id] = 1;
                      else delete next[s.id];
                      return next;
                    })
                  }
                  className="h-4 w-4 accent-[var(--color-ion)]"
                />
                {s.name}
              </label>
              {min !== undefined ? (
                <label className="flex items-center gap-2 text-xs text-ink-2">
                  At least
                  <select className="rounded-md border border-line bg-abyss/50 px-1.5 py-1 text-sm" value={min} onChange={(e) => setMins((m) => ({ ...m, [s.id]: Number(e.target.value) }))}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </li>
          );
        })}
      </ul>

      <Field label="Open hours" hint="Including holidays. Manage them under Business hours.">
        {(id) => (
          <select id={id} className={inputClass} value={f.businessHoursId} onChange={(e) => set('businessHoursId', e.target.value)}>
            <option value="">Always open</option>
            {hours.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-x-3">
        {num('slaSeconds', 'Answer within (s)', 'Service level target time', 5, 600)}
        {num('slaTargetPct', 'Target (%)', 'Share of calls answered in time', 1, 100)}
        {num('ringSeconds', 'Ring each agent (s)', 'Then the next agent is tried', 5, 60)}
        {num('maxWaitSeconds', 'Longest wait (s)', 'Then "After the longest wait" applies', 30, 3600)}
        {num('wrapSeconds', 'Wrap-up time (s)', 'Used when no outcome is required', 0, 600)}
        {num('priority', 'Priority (1-10)', 'Higher queues are served first', 1, 10)}
      </div>
      <Toggle label="Agents must choose an outcome after each call" checked={f.requireDisposition} onChange={(v) => set('requireDisposition', v)} />
      {f.channel === 'voice' ? (
        <Toggle label="Record every call" checked={f.recordPolicy === 'all'} onChange={(v) => set('recordPolicy', v ? 'all' : 'none')} />
      ) : null}
      {f.channel === 'voice' && f.recordPolicy === 'all' ? (
        <Field label="Recording notice" hint='Played before the greeting, e.g. "This call may be recorded for quality purposes". Upload it under Messages.'>
          {(id) => <PromptSelect id={id} value={f.recordingNotice} onChange={(v) => set('recordingNotice', v)} none="No notice" />}
        </Field>
      ) : null}
      {f.channel === 'voice' && f.recordPolicy === 'all' ? (
        <RetentionField
          value={f.recordingRetentionDays}
          none="Organization default"
          hint="Older recordings, and voicemails filed under this queue, are deleted automatically. The call stays in history."
          onChange={(v) => set('recordingRetentionDays', v)}
        />
      ) : null}

      <AwayFields
        title="When the queue is closed"
        hint="Outside its hours, on a holiday, or when a supervisor closes it in an emergency."
        action={f.closedAction}
        queueId={f.closedQueueId}
        number={f.closedNumber}
        others={queues.filter((q) => q.id !== queue?.id)}
        onChange={(p) => setF((x) => ({ ...x, ...(p.action ? { closedAction: p.action } : {}), ...(p.queueId !== undefined ? { closedQueueId: p.queueId } : {}), ...(p.number !== undefined ? { closedNumber: p.number } : {}) }))}
        prompt={f.closedPrompt}
        onPrompt={(closedPrompt) => setF((x) => ({ ...x, closedPrompt }))}
      />
      <AwayFields
        title="After the longest wait"
        hint={`When a caller has waited ${f.maxWaitSeconds} s without an agent.`}
        action={f.overflowAction}
        queueId={f.overflowQueueId}
        number={f.overflowNumber}
        others={queues.filter((q) => q.id !== queue?.id)}
        onChange={(p) => setF((x) => ({ ...x, ...(p.action ? { overflowAction: p.action } : {}), ...(p.queueId !== undefined ? { overflowQueueId: p.queueId } : {}), ...(p.number !== undefined ? { overflowNumber: p.number } : {}) }))}
      />
    </Drawer>
  );
}

/** Where callers go when the queue is closed or they have waited too long. */
function AwayFields({
  title,
  hint,
  action,
  queueId,
  number,
  others,
  onChange,
  prompt,
  onPrompt,
}: {
  title: string;
  hint: string;
  action: AwayAction;
  queueId: string;
  number: string;
  others: Named[];
  onChange: (p: { action?: AwayAction; queueId?: string; number?: string }) => void;
  prompt?: PromptRef | null;
  onPrompt?: (p: PromptRef | null) => void;
}) {
  return (
    <div className="mt-5 rounded-xl border border-line p-4">
      <p className="text-sm font-medium">{title}</p>
      <p className="mb-3 text-xs text-ink-3">{hint}</p>
      <Field label="Callers">
        {(id) => (
          <select id={id} className={inputClass} value={action} onChange={(e) => onChange({ action: e.target.value as AwayAction })}>
            {AWAY.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      {action === 'queue' ? (
        <Field label="Queue">
          {(id) => (
            <select id={id} className={inputClass} value={queueId} onChange={(e) => onChange({ queueId: e.target.value })}>
              <option value="">Choose…</option>
              {others.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {action === 'number' ? (
        <Field label="Number" hint="Rung through your first carrier. If nobody answers, the call ends.">
          {(id) => <input id={id} inputMode="tel" className={`${inputClass} tabular`} value={number} onChange={(e) => onChange({ number: e.target.value })} />}
        </Field>
      ) : null}
      {action === 'voicemail' ? <p className="mb-4 text-xs text-ink-3">Messages are filed under this queue; supervisors find them in Recordings and History.</p> : null}
      {onPrompt ? (
        <Field label="Message first" hint="What callers hear before the above. Upload your own under Messages.">
          {(id) => <PromptSelect id={id} value={prompt ?? null} onChange={onPrompt} none={action === 'hangup' ? '"Goodbye" (default)' : 'Nothing'} />}
        </Field>
      ) : null}
    </div>
  );
}

/** Picks a message: one the organization uploaded, or a built-in sound. */
function PromptSelect({ id, value, onChange, none }: { id: string; value: PromptRef | null; onChange: (p: PromptRef | null) => void; none: string }) {
  const [prompts] = useLoad<Prompt[]>('/prompts');
  const current = value ? (value.kind === 'prompt' ? `p:${value.id}` : `s:${value.name}`) : '';
  return (
    <select
      id={id}
      className={inputClass}
      value={current}
      onChange={(e) => {
        const v = e.target.value;
        onChange(!v ? null : v.startsWith('p:') ? { kind: 'prompt', id: v.slice(2) } : { kind: 'sound', name: v.slice(2) });
      }}
    >
      <option value="">{none}</option>
      {(prompts ?? []).length ? (
        <optgroup label="Your messages">
          {(prompts ?? []).map((p) => (
            <option key={p.id} value={`p:${p.id}`}>
              {p.name} ({LANGUAGES[p.language]})
            </option>
          ))}
        </optgroup>
      ) : null}
      <optgroup label="Built-in (English)">
        {BUILT_IN_SOUNDS.map((b) => (
          <option key={b.name} value={`s:${b.name}`}>
            {b.text}
          </option>
        ))}
      </optgroup>
    </select>
  );
}
